import React from "react";
import { contactInfoTypeLabel } from "../profiles/contact_info_editor/contact_info_types";

const ICONS = {
  email: "fa-solid fa-envelope",
  twitter: "fa-brands fa-x-twitter",
  facebook: "fa-brands fa-facebook",
  instagram: "fa-brands fa-instagram",
  url: "fa-solid fa-globe",
  phone: "fa-solid fa-phone",
  linkedin: "fa-brands fa-linkedin",
  physical_address: "fa-solid fa-map-marker-alt",
};

const hasScheme = (value) => /^[a-z][a-z0-9+.-]*:\/\//i.test(value);

const isHttp = (value) => /^https?:\/\//i.test(value);

// Only web URLs are linkable: a schemeless domain gets https bolted on, but a value carrying any
// other scheme (javascript:, ftp:, ...) is not a destination this card will hand to the browser --
// contact values are user-supplied on some engines, so a foreign scheme renders as plain text.
const withScheme = (value) => {
  if (isHttp(value)) return value;
  if (hasScheme(value)) return null;

  return `https://${value}`;
};

// One classifier decides handle-shaped vs URL-shaped, and both the display label and hrefFor's
// social branch consult it, so the visible text and the destination never disagree about what kind
// of value this is. URL-shaped means: carries a scheme, starts with "www.", contains a path
// separator, or IS a bare social host ("twitter.com", "x.com", "instagram.com" -- a dot alone can't
// be the test, because dots are legal in Instagram handles like "some.name"). Everything else is a
// handle.
const SOCIAL_HOSTS = /^(?:https?:\/\/)?(?:www\.)?(?:twitter\.com|x\.com|instagram\.com)(?:\/|$)/i;

const isUrlShaped = (value) =>
  hasScheme(value) || /^www\./i.test(value) || value.includes("/") || SOCIAL_HOSTS.test(value);

const stripSchemeAndWww = (value) => value.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").replace(/^www\./i, "");

// Handle-style networks store either a bare handle ("@ada", "ada") or a whole profile URL, depending
// on which form captured the value. Only a handle needs a profile URL built around it; anything
// URL-shaped is already a stored profile URL and just needs a scheme.
const profileUrl = (host, value) =>
  isUrlShaped(value) ? withScheme(value) : `https://${host}/${value.replace(/^@/, "")}`;

// The full text for a contact row: a handle keeps (or gains) its "@" prefix, and anything
// URL-shaped drops its scheme and a leading "www." so a stored full profile URL doesn't read
// differently from a handle. Email, phone, and physical address render verbatim -- there is no
// display normalization for them.
function fullLabel(type, value) {
  if (type === "twitter" || type === "instagram") {
    if (!isUrlShaped(value)) return value.startsWith("@") ? value : `@${value}`;
    return stripSchemeAndWww(value);
  }
  if (type === "email" || type === "phone" || type === "physical_address") return value;
  return stripSchemeAndWww(value);
}

// A long website keeps both ends (where two links to one site differ), e.g. "interaction-design…r-better-ux".
const WEBSITE_MAX = 30;
const shortWebsite = (label) =>
  label.length <= WEBSITE_MAX ? label : `${label.slice(0, 18)}…${label.slice(-(WEBSITE_MAX - 19))}`;

// A stored value is not a usable href on its own: an email or phone number needs its own scheme, a
// schemeless domain would resolve as a relative path, and a street address is not a destination at
// all. Returns null for anything that should render as plain text.
function hrefFor(type, value) {
  switch (type) {
    case "email":
      return `mailto:${value}`;
    case "phone":
      return `tel:${value.replace(/[^+\d]/g, "")}`;
    case "physical_address":
      return null;
    case "twitter":
      return profileUrl("twitter.com", value);
    case "instagram":
      return profileUrl("instagram.com", value);
    default:
      return withScheme(value);
  }
}

// Two id_contact_infos rows can hold the identical (type, value) pair -- e.g. two migrated
// identities of one hybrid entity carrying the same Twitter handle. That's noise, so it collapses
// on display. Duplicate *types* are still real data, though: id_contact_infos has no uniqueness
// constraint, and the migration from the legacy system kept every row, so an entity can legitimately
// hold two different linkedin values -- this only collapses rows that agree on both type and value.
function dedupeByTypeAndValue(rows) {
  const seen = new Set();
  return rows.filter(({ type, value }) => {
    const key = `${type}:${value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Websites are text rows too (Alyssa's Connect design), after email, phone and address.
const TEXT_TYPES = new Set(["email", "phone", "physical_address", "url"]);
const TEXT_ORDER = ["email", "phone", "physical_address", "url"];
const textRank = (type) => (TEXT_ORDER.includes(type) ? TEXT_ORDER.indexOf(type) : TEXT_ORDER.length);

// Figma's order for the icon row; any other link types follow.
const ICON_ORDER = ["linkedin", "instagram", "facebook", "twitter"];
const iconRank = (type) => (ICON_ORDER.includes(type) ? ICON_ORDER.indexOf(type) : ICON_ORDER.length);

// Figma's "Connect with <name>" card: email, phone and address as text rows, then every web link as
// a row of icons, labelled by network for screen readers, with the full address as the tooltip.
function ContactDetails({ contactDetails = [] }) {
  if (!contactDetails) {
    return null;
  }

  const rows = dedupeByTypeAndValue(contactDetails).map(({ type, value }) => ({
    type,
    value,
    href: hrefFor(type, value),
    full: fullLabel(type, value),
  }));
  // A link this card won't hand to the browser (a foreign scheme) stays readable as a text row.
  const textRows = rows
    .filter((row) => TEXT_TYPES.has(row.type) || !row.href)
    .sort((a, b) => textRank(a.type) - textRank(b.type));
  const iconRows = rows
    .filter((row) => !TEXT_TYPES.has(row.type) && row.href)
    .sort((a, b) => iconRank(a.type) - iconRank(b.type));

  return (
    <div className="d-flex flex-column gap-3">
      {/* The dedupe above guarantees no two surviving rows share a (type, value) pair. */}
      {textRows.map(({ type, value, href, full }) =>
        type === "url" && href ? (
          <div
            key={`${type}:${value}`}
            className="connect-detail connect-detail-url d-flex align-items-center gap-2 min-w-0"
          >
            <i className={`${ICONS.url} connect-detail-icon`} aria-hidden="true" />
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-truncate" title={full}>
              {shortWebsite(full)}
            </a>
          </div>
        ) : (
          <div key={`${type}:${value}`} className="connect-detail d-flex align-items-center gap-2 text-break">
            <i className={`${ICONS[type] || ICONS.url} connect-detail-icon`} aria-hidden="true" />
            {href ? <a href={href}>{full}</a> : <span>{full}</span>}
          </div>
        ),
      )}
      {textRows.length > 0 && iconRows.length > 0 && <hr className="connect-divider m-0" />}
      {iconRows.length > 0 && (
        <div className="d-flex flex-wrap gap-4">
          {iconRows.map(({ type, value, href, full }) => (
            <a
              key={`${type}:${value}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="connect-icon"
              aria-label={contactInfoTypeLabel(type)}
              title={full}
            >
              <i className={ICONS[type] || ICONS.url} aria-hidden="true" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default ContactDetails;
